import { edgeId } from '../edges.js';
import { FACE_NOT_RENDERABLE, triangleRenderability } from '../validation.js';
import { cross3, dot3, sub3 } from '../vector-math.js';

const VOLUME_AMBIGUITY_RATIO = 1e-12;

function assertTopologyFaces(topology) {
  if (!topology || !Array.isArray(topology.vertices) || !Array.isArray(topology.faces)) {
    throw new Error('orientFacesOutside requires topology vertices and faces arrays');
  }
  const vertices = new Map();
  for (const vertex of topology.vertices) {
    if (!vertex || typeof vertex.id !== 'string' || !Array.isArray(vertex.position) || vertex.position.length !== 3
      || !vertex.position.every(Number.isFinite) || vertices.has(vertex.id)) {
      throw new Error('orientFacesOutside found a malformed or non-finite vertex');
    }
    vertices.set(vertex.id, vertex.position);
  }
  if (topology.faces.length === 0) throw new Error('orientFacesOutside requires at least one face');

  const faceById = new Map();
  for (const face of topology.faces) {
    if (!face || typeof face.id !== 'string' || faceById.has(face.id)
      || !Array.isArray(face.vertexIds) || face.vertexIds.length !== 3
      || new Set(face.vertexIds).size !== 3 || !face.vertexIds.every((id) => typeof id === 'string' && vertices.has(id))) {
      throw new Error('orientFacesOutside found a malformed or degenerate face');
    }
    const positions = face.vertexIds.map((id) => vertices.get(id));
    const renderability = triangleRenderability(positions, { rounded: false });
    if (!renderability.renderable) {
      // Same message as before, now carrying the shared marker every other
      // "this face has no usable normal" failure already carries. Callers that
      // need to tell an unrenderable face apart from a structurally corrupt
      // one — meshDiagnostics most of all — can then discriminate on the code
      // instead of matching message text.
      const error = new Error(`orientFacesOutside found a ${renderability.reason === 'non-finite' ? 'non-finite' : 'degenerate'} face '${face.id}'`);
      error.code = FACE_NOT_RENDERABLE;
      throw error;
    }
    faceById.set(face.id, face);
  }
  return { vertices, faceById };
}

/**
 * Consistently orient every closed, canonical-topology-connected component
 * outward. This deliberately uses only shared vertex ids: coincident render
 * vertices are not a topological connection and must never be position-welded
 * here (see adjacency.js for the broader geometric-adjacency rationale).
 */
export function orientFacesOutside(topology) {
  const { vertices, faceById } = assertTopologyFaces(topology);
  const occurrencesByEdge = new Map();
  for (const face of topology.faces) {
    for (let index = 0; index < 3; index++) {
      const a = face.vertexIds[index];
      const b = face.vertexIds[(index + 1) % 3];
      const id = edgeId(a, b);
      const canonical = [a, b].sort();
      const occurrence = { faceId: face.id, direction: a === canonical[0] ? 1 : -1 };
      const entries = occurrencesByEdge.get(id) ?? [];
      entries.push(occurrence);
      occurrencesByEdge.set(id, entries);
    }
  }
  for (const [id, occurrences] of occurrencesByEdge) {
    if (occurrences.length === 1) throw new Error(`orientFacesOutside cannot orient open component: edge '${id}' has one occurrence`);
    if (occurrences.length > 2) throw new Error(`orientFacesOutside cannot orient non-manifold edge '${id}' with ${occurrences.length} occurrences`);
  }

  const neighborsByFace = new Map([...faceById.keys()].map((id) => [id, []]));
  for (const [edge, occurrences] of occurrencesByEdge) {
    const [a, b] = occurrences;
    const parityDelta = a.direction === b.direction ? 1 : 0;
    neighborsByFace.get(a.faceId).push({ edge, faceId: b.faceId, parityDelta });
    neighborsByFace.get(b.faceId).push({ edge, faceId: a.faceId, parityDelta });
  }
  for (const neighbors of neighborsByFace.values()) neighbors.sort((a, b) => a.edge.localeCompare(b.edge) || a.faceId.localeCompare(b.faceId));

  const parityByFace = new Map();
  const components = [];
  for (const seed of [...faceById.keys()].sort()) {
    if (parityByFace.has(seed)) continue;
    const component = [];
    parityByFace.set(seed, 0);
    const pending = [seed];
    for (let cursor = 0; cursor < pending.length; cursor++) {
      const faceId = pending[cursor];
      component.push(faceId);
      const parity = parityByFace.get(faceId);
      for (const neighbor of neighborsByFace.get(faceId)) {
        const expected = parity ^ neighbor.parityDelta;
        if (parityByFace.has(neighbor.faceId)) {
          if (parityByFace.get(neighbor.faceId) !== expected) {
            throw new Error(`orientFacesOutside found a non-orientable or conflicting topology at edge '${neighbor.edge}'`);
          }
        } else {
          parityByFace.set(neighbor.faceId, expected);
          pending.push(neighbor.faceId);
        }
      }
    }
    components.push(component.sort());
  }

  for (const component of components) {
    const vertexIds = [...new Set(component.flatMap((id) => faceById.get(id).vertexIds))].sort();
    const reference = vertexIds.reduce((sum, id) => sum.map((value, axis) => value + vertices.get(id)[axis]), [0, 0, 0])
      .map((value) => value / vertexIds.length);
    let signedSixVolume = 0;
    let absoluteContribution = 0;
    for (const faceId of component) {
      const face = faceById.get(faceId);
      const ids = parityByFace.get(faceId) ? [face.vertexIds[0], face.vertexIds[2], face.vertexIds[1]] : face.vertexIds;
      const [a, b, c] = ids.map((id) => sub3(vertices.get(id), reference));
      const contribution = dot3(a, cross3(b, c));
      signedSixVolume += contribution;
      absoluteContribution += Math.abs(contribution);
    }
    if (!Number.isFinite(signedSixVolume) || !Number.isFinite(absoluteContribution)
      || signedSixVolume === 0 || Math.abs(signedSixVolume) <= VOLUME_AMBIGUITY_RATIO * absoluteContribution) {
      throw new Error('orientFacesOutside found a zero, ambiguous, or non-finite enclosed component volume');
    }
    if (signedSixVolume < 0) {
      for (const faceId of component) parityByFace.set(faceId, parityByFace.get(faceId) ^ 1);
    }
  }

  const flippedFaceIds = [...parityByFace.entries()].filter(([, parity]) => parity).map(([id]) => id).sort();
  // All validation and planning above completes before the first write.
  for (const face of topology.faces) {
    if (parityByFace.get(face.id)) [face.vertexIds[1], face.vertexIds[2]] = [face.vertexIds[2], face.vertexIds[1]];
  }
  return { componentCount: components.length, flippedFaceIds };
}
